import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Eye, Heart, ChatCircle, Article } from "@phosphor-icons/react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";

const Stat = ({ label, value, icon: Icon, testid }) => (
  <div className="border border-border bg-card p-6" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="eyebrow text-muted-foreground">{label}</div>
      <Icon size={18} weight="duotone" />
    </div>
    <div className="font-serif text-5xl font-black tracking-tight mt-3">{value}</div>
  </div>
);

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/analytics/overview");
      setData(data);
    })();
  }, []);

  if (!data) return <div className="text-muted-foreground">Loading...</div>;
  const t = data.totals;

  return (
    <div>
      <div className="mb-8">
        <div className="eyebrow text-muted-foreground mb-2">Last 14 days</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-black tracking-tight">Analytics</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Articles" value={t.total_articles || 0} icon={Article} testid="stat-articles" />
        <Stat label="Views" value={t.total_views || 0} icon={Eye} testid="stat-views" />
        <Stat label="Likes" value={t.total_likes || 0} icon={Heart} testid="stat-likes" />
        <Stat label="Comments" value={t.total_comments || 0} icon={ChatCircle} testid="stat-comments" />
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8 border border-border bg-card p-6">
          <div className="eyebrow text-muted-foreground mb-4">Views, last 14 days</div>
          <div className="h-72" data-testid="views-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.views_series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 0, fontSize: 12 }} />
                <Line type="monotone" dataKey="views" stroke="hsl(var(--foreground))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-4 border border-border bg-card p-6">
          <div className="eyebrow text-muted-foreground mb-4">Top articles</div>
          {data.top_articles.length === 0 ? (
            <div className="text-sm text-muted-foreground">No articles yet.</div>
          ) : (
            <ul className="space-y-3">
              {data.top_articles.map((a, i) => (
                <li key={a.id} className="flex items-start gap-3" data-testid={`top-article-${i}`}>
                  <span className="font-serif text-2xl font-black text-muted-foreground w-6">{i + 1}</span>
                  <div>
                    <div className="font-serif text-base leading-tight">{a.title}</div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3">
                      <span className="flex items-center gap-1"><Eye size={12} /> {a.views}</span>
                      <span className="flex items-center gap-1"><Heart size={12} /> {a.likes_count}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
